import { TICK_MS } from "../movement/step.ts";
import type { Ability, Item, ResourceId, Rules } from "../rules/types.ts";
import { consumableHeal } from "./consumable.ts";

/**
 * An item's description with placeholders (ADR-091's amendment of 2026-10-09, P2.8-T56): a description may name
 * tokens such as `{restore.health}`, each filled from the item's own stats wherever the description is shown, so the
 * text follows the data. The tokens an item may name are derived from `STAT_KINDS`, one entry per kind of stat an item
 * can carry, never from a list kept by hand; `validateRules` refuses a description naming any other. Pure: every value
 * is a fact of the rules tables, never of the moment's state.
 */

/**
 * One token an item's stats supply: its name as written between braces, what it means (for the Item Creator's list)
 * and its value as a player reads it.
 */
export interface DescriptionToken {
  readonly name: string;
  readonly meaning: string;
  readonly value: string;
}

/**
 * What a stat kind reads: the item, and the ability record its use runs (null for an item with no use).
 */
export interface StatSource {
  readonly item: Item;
  readonly use: Ability | null;
}

/**
 * A kind of stat an item can carry, and the tokens it supplies for an item that carries it (none for one that does
 * not).
 */
export interface StatKind {
  readonly kind: string;
  readonly tokensOf: (source: StatSource) => readonly DescriptionToken[];
}

/**
 * What a description's tokens are: each name in order, or the first brace outside a well-formed token, quoted from
 * that brace to the next `}` or the text's end.
 */
export type DescriptionTokens = { readonly ok: true; readonly names: readonly string[] } | { readonly ok: false; readonly stray: string };

/**
 * A resource a record restores at once, and where its amount is read: a heal restores health, `restoreStamina`
 * stamina (ADR-091's note for P2.8-T57); a later record field that restores another resource is one more entry.
 */
interface RestoreSource {
  readonly resource: ResourceId;
  readonly amountOf: (use: Ability) => number | null;
}

/**
 * The health a record's heal restores at once.
 *
 * @param use The record.
 * @returns The amount, or null for a record with no heal.
 */
function healAmount(use: Ability): number | null {
  return use.heal?.amount ?? null;
}

/**
 * The stamina a record restores at once.
 *
 * @param use The record.
 * @returns The amount, or null for a record that restores none.
 */
function staminaAmount(use: Ability): number | null {
  return use.restoreStamina?.amount ?? null;
}

const RESTORE_SOURCES: readonly RestoreSource[] = [
  { resource: "health", amountOf: healAmount },
  { resource: "stamina", amountOf: staminaAmount },
];

const TOKEN_AT = /\{([a-z][a-z0-9_]*(?:\.[a-z][a-z0-9_]*)*)\}/y;
const BRACE = /[{}]/g;
const MS_PER_SECOND = 1000;
const SECONDS_PER_MINUTE = 60;

/**
 * A count with its unit, singular for one.
 *
 * @param count The count as text.
 * @param unit The singular unit.
 * @returns The words, "1 second" or "30 seconds".
 */
function counted(count: string, unit: string): string {
  return count === "1" ? `1 ${unit}` : `${count} ${unit}s`;
}

/**
 * A duration in words, as a description reads it: seconds to a tenth while that rounds below a minute, else whole
 * minutes and any seconds, so 59.95 seconds reads "1 minute".
 *
 * @param ticks The duration in ticks.
 * @returns The words.
 * @example
 * durationWords(600);
 * // "30 seconds"
 * durationWords(1800);
 * // "1 minute 30 seconds"
 */
export function durationWords(ticks: number): string {
  const ms = ticks * TICK_MS;
  const tenths = Number((ms / MS_PER_SECOND).toFixed(1));
  if (tenths < SECONDS_PER_MINUTE) {
    return counted(String(tenths), "second");
  }
  const seconds = Math.round(ms / MS_PER_SECOND);
  const minutes = counted(String(Math.floor(seconds / SECONDS_PER_MINUTE)), "minute");
  const rest = seconds % SECONDS_PER_MINUTE;
  return rest === 0 ? minutes : `${minutes} ${counted(String(rest), "second")}`;
}

/**
 * The `restore` kind's tokens: for each resource the use restores at once, `{restore.<resource>}` (the amount) and
 * `{restore.<resource>.players}` (the amount while fighting players, the rules' own `consumableHeal`).
 *
 * @param source The item and its use.
 * @returns The tokens, none for an item with no use or a use that restores nothing.
 */
function restoreTokens({ use }: StatSource): readonly DescriptionToken[] {
  if (use === null) {
    return [];
  }
  const tokens: DescriptionToken[] = [];
  for (const { resource, amountOf } of RESTORE_SOURCES) {
    const amount = amountOf(use);
    if (amount !== null) {
      tokens.push({ name: `restore.${resource}`, meaning: `The ${resource} it restores at once`, value: String(amount) });
      tokens.push({ name: `restore.${resource}.players`, meaning: `The ${resource} it restores at once while fighting players`, value: String(consumableHeal(amount, true)) });
    }
  }
  return tokens;
}

/**
 * The `shorten` kind's tokens (ADR-091's note for P2.8-T57, item 6): for each effect the use cuts the time left of,
 * `{shorten.<effect>}`, the percent it cuts with a percent mark and no sign, "15%".
 *
 * @param source The item and its use.
 * @returns The tokens, none for an item with no use or a use that shortens nothing.
 */
function shortenTokens({ use }: StatSource): readonly DescriptionToken[] {
  return use === null ? [] : use.shortens.map(({ effect, percent }) => ({ name: `shorten.${effect}`, meaning: `The share of the time left on ${effect} it cuts`, value: `${percent}%` }));
}

/**
 * The `cooldown` kind's token: `{cooldown}`, the use's cooldown in words.
 *
 * @param source The item and its use.
 * @returns The token, none for an item with no use or a use with no cooldown.
 */
function cooldownTokens({ use }: StatSource): readonly DescriptionToken[] {
  return use === null || use.cooldownTicks <= 0 ? [] : [{ name: "cooldown", meaning: "The time until it can be used again, in words", value: durationWords(use.cooldownTicks) }];
}

/**
 * The stat kinds an item can carry, in the order their tokens are listed: `restore` (`restoreTokens`), `shorten`
 * (`shortenTokens`) and `cooldown` (`cooldownTokens`).
 */
export const STAT_KINDS: readonly StatKind[] = [
  { kind: "restore", tokensOf: restoreTokens },
  { kind: "shorten", tokensOf: shortenTokens },
  { kind: "cooldown", tokensOf: cooldownTokens },
];

/**
 * The tokens an item's stats supply, every stat kind's in `STAT_KINDS` order: what the Item Creator offers an author.
 *
 * @param item The item.
 * @param use The ability record its use runs, or null for an item with no use.
 * @returns The tokens.
 * @example
 * itemTokens(potion, drink).map(({ name }) => name);
 * // ["restore.health", "restore.health.players", "cooldown"]
 */
export function itemTokens(item: Item, use: Ability | null): readonly DescriptionToken[] {
  return STAT_KINDS.flatMap((kind) => kind.tokensOf({ item, use }));
}

/**
 * The tokens a description names, in order. Every `{` starts a token and every `}` ends one, so a brace outside a
 * well-formed token is refused.
 *
 * @param text The description as written.
 * @returns Each token's name, or the stray brace quoted to the next `}` or the text's end.
 * @example
 * tokensIn("Restores {restore.health} health.");
 * // { ok: true, names: ["restore.health"] }
 */
export function tokensIn(text: string): DescriptionTokens {
  const names: string[] = [];
  BRACE.lastIndex = 0;
  for (let brace = BRACE.exec(text); brace !== null; brace = BRACE.exec(text)) {
    TOKEN_AT.lastIndex = brace.index;
    const token = brace[0] === "{" ? TOKEN_AT.exec(text) : null;
    if (token === null) {
      const close = text.indexOf("}", brace.index);
      return { ok: false, stray: close === -1 ? text.slice(brace.index) : text.slice(brace.index, close + 1) };
    }
    names.push(token[1] as string);
    BRACE.lastIndex = TOKEN_AT.lastIndex;
  }
  return { ok: true, names };
}

/**
 * A description with each token replaced by its value.
 *
 * @param text The description as written.
 * @param tokens The tokens its item's stats supply.
 * @returns The description as a player reads it.
 * @throws RangeError when the text holds a stray brace or names a token the tokens lack, which validated rules never do.
 * @example
 * fillDescription("Restores {restore.health} health.", itemTokens(potion, drink));
 * // "Restores 30 health."
 */
export function fillDescription(text: string, tokens: readonly DescriptionToken[]): string {
  const named = tokensIn(text);
  if (!named.ok) {
    throw new RangeError(`the description holds the brace in "${named.stray}" outside a token`);
  }
  for (const name of named.names) {
    if (!tokens.some((token) => token.name === name)) {
      throw new RangeError(`the description names {${name}}, which the tokens do not supply`);
    }
  }
  return text.replace(/\{([a-z0-9_.]+)\}/g, (_whole: string, name: string) => tokens.find((token) => token.name === name)?.value ?? "");
}

/**
 * An item's description as a player reads it, its tokens filled from its own stats: what every surface that shows a
 * description draws.
 *
 * @param rules The rules the item is read from.
 * @param item The item.
 * @returns The filled description.
 * @throws RangeError as `fillDescription` does, which validated rules never reach.
 */
export function itemDescription(rules: Rules, item: Item): string {
  const use = item.use === null ? null : (rules.abilities.get(item.use) ?? null);
  return fillDescription(item.description, itemTokens(item, use));
}
